import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { reportsAPI } from '../../../api';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import { Card, CardContent } from '../../../components/ui/Card';
import { ArrowLeft } from 'lucide-react';

const currency = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n || 0);
const toInputDate = (d) => new Date(d).toISOString().split('T')[0];
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '';

export default function DebtorCreditorReport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [type, setType] = useState('debtor');
  const today = useMemo(() => new Date(), []);
  const [asOf, setAsOf] = useState(toInputDate(today));
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await reportsAPI.getDebtorCreditor({ as_of: asOf, type });
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchData();
  };

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/reports')}
                className="mb-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Reports
              </Button>
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                {type === 'debtor' ? 'Debtor Report' : 'Creditor Report'}
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">Outstanding {type === 'debtor' ? 'invoices' : 'purchases'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)] mb-4">{error}</div>
        )}

        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <FormLabel>Type</FormLabel>
                <Select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="h-8"
                >
                  <option value="debtor">Debtor (Invoices)</option>
                  <option value="creditor">Creditor (Purchases)</option>
                </Select>
              </div>
              <div>
                <FormLabel>As of</FormLabel>
                <Input
                  type="date"
                  value={asOf}
                  onChange={(e) => setAsOf(e.target.value)}
                  className="h-8"
                />
              </div>
              <div className="flex items-end">
                <Button variant="primary" size="sm" onClick={handleApplyFilters} className="w-full">Apply</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
              <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading...</p>
            </div>
          ) : data && (
            <CardContent className="p-6">
              {!data.items || data.items.length === 0 ? (
                <p className="text-center text-xs text-[var(--ds-text-secondary)] py-8">No {type === 'debtor' ? 'outstanding invoices' : 'outstanding purchases'} found</p>
              ) : (
                <>
                  <div className="mb-4 pb-4 border-b border-[var(--ds-border-divider)]">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-semibold text-[var(--ds-text-primary)]">Total Outstanding:</span>
                      <span className="text-xs font-bold tabular-nums text-[var(--ds-text-primary)]">{currency(data.total || 0)}</span>
                    </div>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow isHeader>
                        <TableHead align="left">
                          {type === 'debtor' ? 'Invoice #' : 'Purchase #'}
                        </TableHead>
                        <TableHead align="left">
                          {type === 'debtor' ? 'Client' : 'Supplier'}
                        </TableHead>
                        <TableHead align="left">
                          {type === 'debtor' ? 'Due Date' : 'Purchase Date'}
                        </TableHead>
                        <TableHead align="right">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell align="left">{type === 'debtor' ? item.invoiceNum : item.purchaseNum}</TableCell>
                          <TableCell align="left">
                            {type === 'debtor' ? item.clientName : item.supplierName}
                          </TableCell>
                          <TableCell align="left">
                            {formatDate(type === 'debtor' ? item.dueDate : item.purchaseDate)}
                          </TableCell>
                          <TableCell align="right" className="tabular-nums">{currency(item.balanceDue || 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          )}
        </Card>
      </div>
    </Layout>
  );
}
